import React, { useState } from 'react'
import { useRevenueAnalytics, useRevenueByRestaurant } from '@/hooks/admin/useAnalytics'
import { TrendingUp, DollarSign, Calendar, Building2 } from 'lucide-react'
import { formatCurrency } from '@/utils/currency'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

const PLAN_COLORS = {
  FREE: '#9CA3AF',
  STARTER: '#3B82F6',
  PRO: '#A855F7',
  ENTERPRISE: '#EAB308',
}

export default function AdminRevenuePage() {
  const [period, setPeriod] = useState<'30' | '90' | '365'>('30')
  const [groupBy, setGroupBy] = useState<'DAY' | 'WEEK' | 'MONTH'>('DAY')

  const endDate = new Date().toISOString().split('T')[0]
  const startDate = new Date(Date.now() - parseInt(period) * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]

  const { data: analytics, isLoading, error } = useRevenueAnalytics({
    startDate,
    endDate,
    groupBy,
  })

  const { data: restaurantRevenue, isLoading: isLoadingRestaurants } = useRevenueByRestaurant()

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Analisi Revenue
        </h1>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-600 dark:text-red-400">
            Errore nel caricamento analytics: {error.message}
          </p>
        </div>
      </div>
    )
  }

  if (isLoading || !analytics || !analytics.dailyRevenue) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Analisi Revenue
        </h1>
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-4">Caricamento analytics...</p>
        </div>
      </div>
    )
  }

  // Prepare data for charts with safe defaults
  const revenueChartData = (analytics.dailyRevenue || []).map((item) => ({
    date: new Date(item.date).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
    }),
    subscriptions: item.subscriptionRevenue,
    commissions: item.commissionRevenue,
    total: item.totalRevenue,
  }))

  const planDistributionData = [
    { name: 'FREE', value: analytics.revenueByPlan?.FREE || 0, color: PLAN_COLORS.FREE },
    { name: 'STARTER', value: analytics.revenueByPlan?.STARTER || 0, color: PLAN_COLORS.STARTER },
    { name: 'PRO', value: analytics.revenueByPlan?.PRO || 0, color: PLAN_COLORS.PRO },
    { name: 'ENTERPRISE', value: analytics.revenueByPlan?.ENTERPRISE || 0, color: PLAN_COLORS.ENTERPRISE },
  ].filter((item) => item.value > 0)

  const topRestaurantsData = (analytics.topRevenueRestaurants || []).map((r) => ({
    name: r.restaurantName.length > 15 ? r.restaurantName.substring(0, 15) + '...' : r.restaurantName,
    revenue: r.totalRevenue,
  }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Analisi Revenue
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Panoramica completa dei ricavi della piattaforma
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Periodo
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setPeriod('30')
                  setGroupBy('DAY')
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  period === '30'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                30 giorni
              </button>
              <button
                onClick={() => {
                  setPeriod('90')
                  setGroupBy('WEEK')
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  period === '90'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                90 giorni
              </button>
              <button
                onClick={() => {
                  setPeriod('365')
                  setGroupBy('MONTH')
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  period === '365'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                12 mesi
              </button>
            </div>
          </div>
          <div className="flex items-end gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Calendar className="w-4 h-4" />
            <span>
              Dal {new Date(startDate).toLocaleDateString('it-IT')} al{' '}
              {new Date(endDate).toLocaleDateString('it-IT')}
            </span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Revenue Totale
            </h3>
            <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(analytics.totalRevenue)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Periodo selezionato
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Revenue Sottoscrizioni
            </h3>
            <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(analytics.subscriptionRevenue)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {analytics.totalRevenue > 0
              ? ((analytics.subscriptionRevenue / analytics.totalRevenue) * 100).toFixed(1)
              : 0}% del totale
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Commissioni Ordini
            </h3>
            <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(analytics.commissionRevenue)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {analytics.totalRevenue > 0
              ? ((analytics.commissionRevenue / analytics.totalRevenue) * 100).toFixed(1)
              : 0}% del totale
          </p>
        </div>
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Trend Revenue nel Tempo
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={revenueChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
            <XAxis
              dataKey="date"
              stroke="#9CA3AF"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#9CA3AF"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `€${value}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
              }}
              formatter={(value: number) => formatCurrency(value)}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="subscriptions"
              stroke="#3B82F6"
              strokeWidth={2}
              name="Sottoscrizioni"
              dot={{ fill: '#3B82F6' }}
            />
            <Line
              type="monotone"
              dataKey="commissions"
              stroke="#A855F7"
              strokeWidth={2}
              name="Commissioni"
              dot={{ fill: '#A855F7' }}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#10B981"
              strokeWidth={3}
              name="Totale"
              dot={{ fill: '#10B981' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Plan */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Revenue per Piano
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={planDistributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {planDistributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {planDistributionData.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {item.name}: {formatCurrency(item.value)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Revenue Restaurants */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Top 10 Ristoranti per Revenue
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topRestaurantsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis
                dataKey="name"
                stroke="#9CA3AF"
                style={{ fontSize: '11px' }}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `€${value}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Bar dataKey="revenue" fill="#10B981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Revenue by Restaurant Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Revenue per Ristorante
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Dettaglio completo di quanto deve ogni ristorante
              </p>
            </div>
          </div>
        </div>

        {isLoadingRestaurants ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-4">Caricamento dati...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ristorante
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Piano
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ordini
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Volume Ordini
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Canone Mensile
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Commissioni
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center justify-end gap-1">
                      <span>Totale da Fatturare</span>
                      <DollarSign className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    % Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {restaurantRevenue?.map((restaurant) => (
                  <tr key={restaurant.restaurantId} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {restaurant.restaurantName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {restaurant.restaurantId.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        restaurant.plan === 'FREE' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                        restaurant.plan === 'STARTER' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                        restaurant.plan === 'PRO' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                      }`}>
                        {restaurant.plan}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {restaurant.totalOrders.toLocaleString('it-IT')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(restaurant.totalOrderVolume)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(restaurant.subscriptionFee)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(restaurant.transactionFees)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(restaurant.totalPlatformRevenue)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                      {restaurant.revenueContributionPercentage
                        ? `${restaurant.revenueContributionPercentage.toFixed(1)}%`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {restaurantRevenue && restaurantRevenue.length > 0 && (
                <tfoot className="bg-gray-50 dark:bg-gray-700 font-semibold">
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      TOTALE ({restaurantRevenue.length} ristoranti)
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(
                        restaurantRevenue.reduce((sum, r) => sum + r.totalOrderVolume, 0)
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(
                        restaurantRevenue.reduce((sum, r) => sum + r.subscriptionFee, 0)
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(
                        restaurantRevenue.reduce((sum, r) => sum + r.transactionFees, 0)
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-lg font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(
                        restaurantRevenue.reduce((sum, r) => sum + r.totalPlatformRevenue, 0)
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white">
                      100%
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
            {(!restaurantRevenue || restaurantRevenue.length === 0) && (
              <div className="text-center py-12">
                <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Nessun dato disponibile
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
